import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  ArrowRight,
  CheckCircle2,
  Eye,
  EyeOff,
  Lock,
  LogIn,
  Mail,
  ShieldCheck,
} from 'lucide-react'
import { toast } from 'sonner'
import { apiJson } from '@/api/client'
import { useBranding } from '@/lib/branding'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { ApiError } from '@/api/client'
import {
  login,
  bvnVerify,
  bvnResend,
  isBVNChallenge,
  requestPasswordReset,
  type BVNChallengeResponse,
  type LoginResult,
} from './api'
import { meQueryKey } from './hooks'
import { pickLandingRoute, validateNext } from './landing'
import type { Me } from './types'

/**
 * Real login form. POSTs `{email, password}` to `/api/v1/auth/login`,
 * captures the access + refresh tokens into the auth-tokens store,
 * invalidates the `useMe` query so the app rehydrates with the new
 * session, then routes to a permission-appropriate landing page.
 *
 * Landing rules:
 *   1. If `?next=` was supplied AND the user can actually open it,
 *      go there (preserves deep-link return after a 401 bounce).
 *   2. Otherwise consult `pickLandingRoute(me)` which walks a
 *      preference ladder (alerts → cases → dashboard → … →
 *      settings) and returns the first route the user has access
 *      to. Avoids the long-standing "everyone lands on /alerts"
 *      bug where roles without `alerts:read` (most analysts,
 *      compliance officers, api_user) hit a 403 on first paint.
 *
 * Platform mode: a platform admin (`me.is_platform_admin === true`) always
 * lands on /my-dashboard, never on a tenant surface — see the
 * `isPlatformMode` doc on `LandingOptions` (landing.ts) for why this is
 * safe to derive from `is_platform_admin` alone at this specific call site
 * (selectedTenantId cannot yet be non-null at the moment of login).
 */
export function LoginPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const qc = useQueryClient()
  const nextParam = searchParams.get('next')
  const branding = useBranding()
  // AF-402 — surface why the user landed back at /login when the
  // session timer or a backend 401 sent them here. Without this
  // banner the user sees a blank login page and assumes the app
  // crashed.
  const reason = searchParams.get('reason')

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [passwordVisible, setPasswordVisible] = useState(false)
  // AF-401 — when /auth/login returns a step-up requirement we
  // park the challenge here and switch the form to the OTP step.
  const [challenge, setChallenge] = useState<BVNChallengeResponse | null>(null)

  // Pulled out of the mutation so we can call it from both the
  // password and OTP paths.
  async function handleLoginSuccess(): Promise<void> {
    let me: Me | undefined
    try {
      me = await apiJson<Me>('/me')
      qc.setQueryData(meQueryKey, me)
    } catch {
      void qc.invalidateQueries({ queryKey: meQueryKey })
    }
    // Platform mode (ADR-0024): selectedTenantId is session-only useState
    // seeded fresh on every page load (PlatformTenantProvider), so at the
    // moment of login it is ALWAYS null — is_platform_admin alone tells us
    // whether this principal is in platform mode. See LandingOptions
    // (landing.ts) for the full rationale.
    const isPlatformMode = me?.is_platform_admin === true
    const safeNext = validateNext(nextParam, me, { isPlatformMode })
    const destination = safeNext ?? pickLandingRoute(me, { isPlatformMode })
    toast.success('Signed in')
    void navigate(destination, { replace: true })
  }

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: async (res: LoginResult) => {
      if (isBVNChallenge(res)) {
        setChallenge(res)
        return
      }
      await handleLoginSuccess()
    },
    onError: (err) => {
      // Backend returns a generic message on bad-creds to avoid email
      // enumeration. Show whatever the server sent verbatim.
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Sign-in failed'
      toast.error(msg)
    },
  })

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!email.trim() || !password) return
    loginMutation.mutate({ email: email.trim(), password })
  }

  // Document title — the global index.html title applies to every
  // route via the SPA shell. Override per-page so the browser tab +
  // screen-reader tab announcement reflect the current page. Bank
  // display_name comes from /api/v1/branding (public endpoint).
  useEffect(() => {
    const prev = document.title
    document.title = `Sign in · ${branding.display_name} Antifraud Workspace`
    return () => {
      document.title = prev
    }
  }, [branding.display_name])

  return (
    // <main> landmark so screen readers + skip-to-content shortcuts
    // can target the primary page region. aria-labelledby anchors
    // the landmark on the page heading.
    <main
      aria-labelledby="login-heading"
      className="flex min-h-screen items-center justify-center bg-muted/30 p-6"
    >
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center gap-2 border-b-0 pb-2 text-center">
          <div
            aria-hidden
            className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary"
          >
            {branding.logo_url ? (
              <img
                src={branding.logo_url}
                alt=""
                className="h-8 w-8 rounded-full object-contain"
                onError={(e) => {
                  // 404 on configured URL → swap to the shield
                  // fallback inline. Two children mode: hide img,
                  // show sibling icon already in DOM order.
                  e.currentTarget.style.display = 'none'
                }}
              />
            ) : (
              <ShieldCheck size={22} />
            )}
          </div>
          {/* CardTitle defaults to <h3>; login is the page's primary
              heading, so promote to <h1> for outline + screen-reader
              rotor navigation. */}
          <CardTitle as="h1" id="login-heading" className="text-lg">
            {branding.display_name} · Fraud Operations
          </CardTitle>
          <p className="text-xs text-muted-fg">Multi-channel monitoring access portal</p>
        </CardHeader>
        <CardContent className="pt-2">
          {reason ? <SignInReasonBanner reason={reason} /> : null}
          {challenge ? (
            <BVNStepUpForm
              challenge={challenge}
              onCancel={() => setChallenge(null)}
              onVerified={handleLoginSuccess}
            />
          ) : (
            <form
              onSubmit={submit}
              aria-label={`Sign in to ${branding.display_name}`}
              className="flex flex-col gap-4"
            >
              <FieldLabel htmlFor="email" required>
                Staff ID / Corporate Email
              </FieldLabel>
              <div className="-mt-3 relative">
                <Mail
                  aria-hidden
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg"
                />
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@bank.example.com"
                  className="pl-9"
                />
              </div>

              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs font-medium">
                  Password <span aria-hidden className="text-destructive">*</span>
                  <span className="sr-only">required</span>
                </Label>
                <Link
                  to="/login/forgot"
                  className="text-xs text-primary hover:underline"
                >
                  Forgot?
                </Link>
              </div>
              <div className="-mt-3 relative">
                <Lock
                  aria-hidden
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg"
                />
                <Input
                  id="password"
                  type={passwordVisible ? 'text' : 'password'}
                  autoComplete="current-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-9 pr-9"
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible((v) => !v)}
                  aria-label={passwordVisible ? 'Hide password' : 'Show password'}
                  aria-pressed={passwordVisible}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-fg hover:bg-muted hover:text-fg"
                >
                  {passwordVisible ? <EyeOff size={14} aria-hidden /> : <Eye size={14} aria-hidden />}
                </button>
              </div>

              <Button
                type="submit"
                disabled={
                  loginMutation.isPending || !email.trim() || password === ''
                }
                className="mt-1"
              >
                {loginMutation.isPending ? (
                  'Signing in…'
                ) : (
                  <>
                    <LogIn size={14} className="mr-2" aria-hidden />
                    Continue to Verification
                    <ArrowRight size={14} className="ml-2" aria-hidden />
                  </>
                )}
              </Button>
            </form>
          )}
          {!challenge ? <SSOSignInButton /> : null}
        </CardContent>
      </Card>
    </main>
  )
}

/** FieldLabel — form-field label with required-state asterisk + sr-only
 *  "required" text. Pure UI helper; doesn't touch validation. */
function FieldLabel({
  htmlFor,
  required,
  children,
}: {
  htmlFor: string
  required?: boolean
  children: React.ReactNode
}) {
  return (
    <Label htmlFor={htmlFor} className="text-xs font-medium">
      {children}
      {required ? (
        <>
          {' '}
          <span aria-hidden className="text-destructive">
            *
          </span>
          <span className="sr-only">required</span>
        </>
      ) : null}
    </Label>
  )
}

// SSO sign-in button — navigates to /api/v1/auth/oidc/login if the
// case-service has OIDC wired (probed via /api/v1/auth/oidc/status).
// Hidden when OIDC isn't enabled so a non-OIDC deployment doesn't
// surface a button that 404s.
function SSOSignInButton() {
  const { data, isLoading } = useQuery({
    queryKey: ['auth', 'oidc', 'status'],
    queryFn: async () => {
      const r = await fetch('/api/v1/auth/oidc/status')
      if (!r.ok) return { enabled: false }
      return (await r.json()) as { enabled: boolean; provider?: string }
    },
    staleTime: 5 * 60_000,
    retry: false,
  })
  if (isLoading || !data?.enabled) return null
  const label = data.provider ? `Sign in with ${data.provider}` : 'Sign in with SSO'
  // Plain <a href> — no Button wrapper, no Radix Slot, no JS click
  // handler. Whatever the React tree does, a vanilla anchor click
  // is a browser-native full-page navigation that nothing can
  // intercept. Classes mirror Button variant=outline size=md.
  return (
    <>
      <div className="my-3 flex items-center gap-2 text-[0.625rem] uppercase tracking-wider text-muted-fg">
        <span className="h-px flex-1 bg-border" />
        or
        <span className="h-px flex-1 bg-border" />
      </div>
      <a
        href="/api/v1/auth/oidc/login"
        className="inline-flex h-9 w-full items-center justify-center gap-2 rounded border border-border bg-transparent px-4 text-sm font-medium text-fg transition-colors hover:bg-muted focus-visible:outline-none"
        data-testid="sso-sign-in"
      >
        {label}
      </a>
    </>
  )
}

// AF-401 — BVN OTP step. Rendered after the password step when the
// backend returned `requires_bvn_step_up: true`. On verify success
// the parent's onVerified runs the same /me-fetch + landing-route
// flow as the no-step-up path.
function BVNStepUpForm({
  challenge,
  onCancel,
  onVerified,
}: {
  challenge: BVNChallengeResponse
  onCancel: () => void
  onVerified: () => Promise<void>
}) {
  const [otp, setOtp] = useState('')

  const verify = useMutation({
    mutationFn: () =>
      bvnVerify({ challenge_id: challenge.challenge_id, otp: otp.trim() }),
    onSuccess: async () => {
      await onVerified()
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Verification failed'
      toast.error(msg)
    },
  })

  const resend = useMutation({
    mutationFn: () => bvnResend(challenge.challenge_id),
    onSuccess: () => toast.success('A new code was sent.'),
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Resend failed'
      toast.error(msg)
    },
  })

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (otp.trim().length !== 6) return
    verify.mutate()
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <div className="flex items-center gap-2 rounded border border-primary/30 bg-primary/5 px-3 py-2 text-xs text-primary">
        <ShieldCheck className="h-4 w-4 shrink-0" />
        <p>
          We sent a 6-digit code to{' '}
          <span className="font-mono">{challenge.phone_masked || 'your phone'}</span>.
        </p>
      </div>
      <div className="flex flex-col gap-1">
        <Label htmlFor="otp">Verification code</Label>
        <Input
          id="otp"
          type="text"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          required
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
          placeholder="123456"
          // eslint-disable-next-line jsx-a11y/no-autofocus -- modal/dialog focus management; primary control on open
          autoFocus
        />
      </div>
      <Button type="submit" disabled={verify.isPending || otp.trim().length !== 6}>
        {verify.isPending ? 'Verifying…' : 'Verify'}
      </Button>
      <div className="flex items-center justify-between text-xs">
        <button
          type="button"
          className="text-muted-fg hover:text-fg hover:underline"
          onClick={onCancel}
        >
          Use a different account
        </button>
        <button
          type="button"
          className="text-primary hover:underline disabled:opacity-50"
          onClick={() => resend.mutate()}
          disabled={resend.isPending}
        >
          {resend.isPending ? 'Sending…' : 'Resend code'}
        </button>
      </div>
    </form>
  )
}

function SignInReasonBanner({ reason }: { reason: string }) {
  const message =
    reason === 'timeout'
      ? 'You were signed out after a period of inactivity. Sign in again to continue.'
      : reason === 'expired'
        ? 'Your session expired. Please sign in again.'
        : reason === 'logout'
          ? 'You have been signed out.'
          : null
  if (!message) return null
  return (
    <div
      role="status"
      className="mb-3 rounded border border-amber-300/60 bg-amber-100/60 px-3 py-2 text-xs text-amber-700 dark:border-amber-500/30 dark:bg-amber-900/30 dark:text-amber-300"
    >
      {message}
    </div>
  )
}

/**
 * AF-407 — request password reset. Mirrors the Sign-in page chrome
 * (shield, heading, subtitle) so the visual transition between the
 * two pages is seamless. Shows a generic confirmation regardless of
 * whether the email exists, so the form doesn't enumerate accounts.
 */
export function ForgotPasswordPage() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const mutation = useMutation({
    mutationFn: (e: string) => requestPasswordReset(e),
    onSuccess: () => {
      setSubmitted(true)
    },
    onError: (err) => {
      const msg =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Could not send reset email'
      toast.error(msg)
    },
  })

  useEffect(() => {
    const prev = document.title
    document.title = 'Reset password · Antifraud Analyst Workspace'
    return () => {
      document.title = prev
    }
  }, [])

  const submit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!email.trim()) return
    mutation.mutate(email.trim())
  }

  return (
    <main
      aria-labelledby="forgot-heading"
      className="flex min-h-screen items-center justify-center bg-muted/30 p-6"
    >
      <Card className="w-full max-w-sm">
        <CardHeader className="items-center gap-2 border-b-0 pb-2 text-center">
          <div
            aria-hidden
            className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary"
          >
            {submitted ? <CheckCircle2 size={22} /> : <ShieldCheck size={22} />}
          </div>
          <CardTitle as="h1" id="forgot-heading" className="text-lg">
            {submitted ? 'Check your inbox' : 'Reset your password'}
          </CardTitle>
          <p className="text-xs text-muted-fg">
            {submitted
              ? 'If the address is registered, a reset link is on its way.'
              : 'We will email a one-time reset link to your registered address.'}
          </p>
        </CardHeader>
        <CardContent className="pt-2">
          {submitted ? (
            <div className="flex flex-col gap-3">
              <p className="rounded border border-border bg-muted/40 p-3 text-xs text-muted-fg">
                The link expires in 30 minutes. If it doesn&apos;t arrive,
                check your spam folder or contact your platform admin.
              </p>
              <Link
                to="/login"
                className="inline-flex h-9 items-center justify-center gap-2 rounded border border-border bg-transparent px-4 text-sm font-medium text-fg transition-colors hover:bg-muted"
              >
                <ArrowLeft size={14} aria-hidden />
                Back to sign in
              </Link>
            </div>
          ) : (
            <form
              onSubmit={submit}
              aria-label="Request password reset"
              className="flex flex-col gap-4"
            >
              <FieldLabel htmlFor="forgot-email" required>
                Corporate email
              </FieldLabel>
              <div className="-mt-3 relative">
                <Mail
                  aria-hidden
                  size={14}
                  className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg"
                />
                <Input
                  id="forgot-email"
                  type="email"
                  autoComplete="username"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@bank.example.com"
                  className="pl-9"
                />
              </div>
              <Button
                type="submit"
                disabled={mutation.isPending || !email.trim()}
                className="mt-1"
              >
                {mutation.isPending ? 'Sending…' : (
                  <>
                    Send reset link
                    <ArrowRight size={14} className="ml-2" aria-hidden />
                  </>
                )}
              </Button>
              <Link
                to="/login"
                className="inline-flex items-center justify-center gap-1 text-xs text-muted-fg hover:text-fg hover:underline"
              >
                <ArrowLeft size={12} aria-hidden />
                Back to sign in
              </Link>
            </form>
          )}
        </CardContent>
      </Card>
    </main>
  )
}
